import { NoImage } from "@assets/constant/index.constant";
import { Switch } from "@components/ui/switch";
import { endpoint } from "@services/api.services";
import { UserProfile } from "@ui/internal/components/card";
import { formatDateTime } from "@utils/helper/index.helper";
import { useHandleAction } from "@utils/hook/index.hook";
import { usePreviewVideo, useSelectionStore } from "@utils/states/index.state";
import { Star } from "lucide-react";
import { useMemo } from "react";
import { NavLink } from "react-router";

const ColumnList = ({
  refetch,
  toggleActiveStatus,
}: {
  refetch?: () => void;
  toggleActiveStatus: (id: string | number, state: boolean) => void;
}) => {
  const TestimonialEndpoint = useMemo(
    () => endpoint["enterprise-testimonials"],
    []
  );
  const { clearSelection } = useSelectionStore();
  const { handleAction } = useHandleAction({
    endpoint: TestimonialEndpoint,
    refetch,
  });
  const columns = useMemo(
    () => [
      {
        name: "ID",
        cell: (row: any) => (
          <span className="font-semibold text-gray-700">{row.id}</span>
        ),
        sortable: true,
      },
      {
        name: "Created Date",
        accessKey: "created_at",
        cell: (row: any) => {
          const date = row.updated_at || row.updated_date || row.created_at || row.created_date;
          return (
            <span className="font-semibold text-gray-700">
              {date ? formatDateTime(date) : "-"}
            </span>
          );
        },
        sortable: true,
      },
      {
        name: "Name & Role",
        cell: (row: any) => {
          return (
            <div className="flex max-w-[250px] w-fit justify-start">
              <UserProfile
                userimage={row.profile_icon || NoImage}
                userdesignation={row?.reviewer_position_course}
                username={row.reviewer_name}
              />
            </div>
          );
        },
        sortable: false,
      },
      {
        name: "Review Text",
        cell: (row: any) => {
          return (
            <p
              className="line-clamp-2 max-w-[350px] w-fit"
              dangerouslySetInnerHTML={{ __html: row?.review_text }}
            ></p>
          );
        },
        sortable: true,
      },
      {
        name: "Rating",
        cell: (row: any) => {
          return (
            <div className="flex items-center gap-1">
              {row?.rating_star ? (
                <>
                  <span className="font-semibold text-gray-700">
                    {row.rating_star}
                  </span>
                  <Star size={14} className="text-yellow-500 fill-yellow-500" />
                </>
              ) : (
                <span className="text-gray-400">-</span>
              )}
            </div>
          );
        },
        sortable: true,
      },
      {
        name: "Embed Link",
        cell: (row: any) => {
          return row?.embed_link ? (
            <a
              href={row.embed_link}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#1DA077] hover:underline max-w-[200px] truncate block"
            >
              {row.embed_link}
            </a>
          ) : (
            <span className="text-gray-400">-</span>
          );
        },
        sortable: false,
      },
      {
        name: "Actions",
        cell: (row: any) => (
          <div className="flex items-center gap-3 cursor-pointer font-[500] select-none">
            <Switch
              defaultChecked={row?.is_show}
              onCheckedChange={(value) => toggleActiveStatus(row?.id, value)}
              className="cursor-pointer"
            />
            <NavLink
              to={`/enterprise-testimonials?page=edit&page-item=${row.id}`}
            >
              <button
                className="text-[#1DA077] cursor-pointer"
                title="Edit"
                type="button"
              >
                Edit
              </button>
            </NavLink>
            <button
              className="text-[#C33241] cursor-pointer"
              onClick={() => {
                clearSelection();
                handleAction({
                  type: "confirm",
                  data: { type: "delete", id: row.id },
                });
              }}
              title="Delete"
            >
              Delete
            </button>
          </div>
        ),
        sortable: false,
      },
    ],
    [handleAction, clearSelection]
  );
  return columns;
};

export { ColumnList };
